import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Alert, Form, Badge, Modal } from 'react-bootstrap';
import { useParams, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import TenantManagementLayout from './TenantManagementLayout';

const ACCOUNT_LEVELS = [
  { value: 'testing', label: 'Testing', tokens: 1000 },
  { value: 'standard', label: 'Standard', tokens: 50000 },
  { value: 'professional', label: 'Professional', tokens: 125000 },
  { value: 'enterprise', label: 'Enterprise', tokens: 300000 }
];

const PRODUCTS = [
  { id: 'article_to_media', name: 'Article to Media', available: true },
  { id: 'audio_to_media', name: 'Audio to Media', available: false },
  { id: 'live_audio_to_media', name: 'Live Audio to Media', available: false }
];

const TenantDetail = ({ auth }) => {
  const { id } = useParams();
  const history = useHistory();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    account_level: 'testing',
    products_enabled: [],
    audio_mode: 'summary',
    audio_duration_summary: 30,
    audio_duration_reading: 30,
    markup_percentage: 200,
    seat_count: 10,
    timezone: 'America/New_York'
  });

  useEffect(() => {
    fetchTenant();
  }, [id]);

  const fetchTenant = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/admin/tenants/${id}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTenant(data.tenant);
        setFormData({
          name: data.tenant.name,
          account_level: data.tenant.account_level,
          products_enabled: data.tenant.settings?.products_enabled || [],
          audio_mode: data.tenant.settings?.audio_mode || 'summary',
          audio_duration_summary: data.tenant.settings?.audio_duration?.summary || 30,
          audio_duration_reading: data.tenant.settings?.audio_duration?.reading_30s || 30,
          markup_percentage: Math.round(parseFloat(data.tenant.markup_percentage || 0) * 100),
          seat_count: data.tenant.seat_count,
          timezone: data.tenant.timezone || 'America/New_York'
        });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch tenant');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Format the data for the API - products_enabled goes in settings
      const updateData = {
        name: formData.name,
        account_level: formData.account_level,
        seat_count: formData.seat_count,
        timezone: formData.timezone,
        markup_percentage: formData.markup_percentage / 100,
        settings: {
          ...(tenant.settings || {}),
          products_enabled: formData.products_enabled,
          audio_mode: formData.audio_mode,
          audio_duration: {
            summary: formData.audio_duration_summary,
            reading_30s: formData.audio_duration_reading
          }
        }
      };

      const response = await fetch(`/api/admin/tenants/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        setSuccess('Tenant updated successfully');
        setEditMode(false);
        fetchTenant();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update tenant');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Are you sure you want to deactivate this tenant? Users will not be able to access it.')) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/tenants/${id}/deactivate`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        setSuccess('Tenant deactivated');
        fetchTenant();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to deactivate tenant');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReactivate = async () => {
    try {
      const response = await fetch(`/api/admin/tenants/${id}/reactivate`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        setSuccess('Tenant reactivated');
        fetchTenant();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to reactivate tenant');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmName !== tenant.name) {
      setError('Tenant name does not match');
      return;
    }

    try {
      const response = await fetch(`/api/admin/tenants/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirmation_name: deleteConfirmName })
      });

      if (response.ok) {
        history.push('/admin/tenants');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete tenant');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleProductToggle = (productId) => {
    const current = formData.products_enabled;
    if (current.includes(productId)) {
      setFormData({
        ...formData,
        products_enabled: current.filter(p => p !== productId)
      });
    } else {
      setFormData({
        ...formData,
        products_enabled: [...current, productId]
      });
    }
  };

  const handleImpersonate = async (role) => {
    try {
      const response = await fetch(`/api/admin/tenants/${id}/impersonate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role })
      });

      if (response.ok) {
        window.location.href = '/dashboard';
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to start impersonation');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const formatNumber = (num) => {
    return (num || 0).toLocaleString();
  };

  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Singapore',
    'Australia/Sydney',
    'UTC'
  ];

  if (loading) {
    return (
      <TenantManagementLayout title="Tenant Details">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading tenant details...</p>
        </div>
      </TenantManagementLayout>
    );
  }

  if (!tenant) {
    return (
      <TenantManagementLayout title="Tenant Not Found">
        <Alert variant="warning">Tenant not found</Alert>
      </TenantManagementLayout>
    );
  }

  return (
    <TenantManagementLayout
      title={tenant.name}
      subtitle={`Slug: ${tenant.slug}`}
    >
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
          {success}
        </Alert>
      )}

      {/* Status Banner */}
      {!tenant.is_active && (
        <Alert variant="warning" className="d-flex justify-content-between align-items-center">
          <span>This tenant is deactivated. Users cannot access it.</span>
          <Button variant="success" size="sm" onClick={handleReactivate}>
            Reactivate
          </Button>
        </Alert>
      )}

      {/* Quick Stats */}
      <Row className="mb-4">
        <Col md={3}>
          <Card style={{ background: '#f0fdf4', border: 'none' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#166534' }}>
                {formatNumber(tenant.monthly_tokens_used || 0)}
              </div>
              <div style={{ color: '#166534', fontSize: '0.9rem' }}>Tokens Used</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: '#eff6ff', border: 'none' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e40af' }}>
                {formatNumber(tenant.monthly_token_allocation)}
              </div>
              <div style={{ color: '#1e40af', fontSize: '0.9rem' }}>Monthly Allocation</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: '#fef3c7', border: 'none' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#92400e' }}>
                {formatNumber(tenant.oneoff_token_balance)}
              </div>
              <div style={{ color: '#92400e', fontSize: '0.9rem' }}>Bonus Tokens</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: '#faf5ff', border: 'none' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#6b21a8' }}>
                {tenant.user_count || 0} / {tenant.seat_count}
              </div>
              <div style={{ color: '#6b21a8', fontSize: '0.9rem' }}>Users / Seats</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Impersonation Quick Actions */}
      {['super_admin', 'admin'].includes(auth.platform_role) && tenant.is_active && (
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Quick Actions</h5>
          </Card.Header>
          <Card.Body>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant="outline-primary"
                onClick={() => handleImpersonate('admin')}
              >
                View as Admin
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => handleImpersonate('user')}
              >
                View as User
              </Button>
              <Button
                variant="outline-info"
                onClick={() => history.push(`/admin/tenants/${id}/tokens`)}
              >
                Manage Tokens
              </Button>
              <Button
                variant="outline-info"
                onClick={() => history.push(`/admin/tenants/${id}/users`)}
              >
                Manage Users
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Tenant Details */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Tenant Details</h5>
          {!editMode && ['super_admin', 'admin'].includes(auth.platform_role) && (
            <Button variant="outline-primary" size="sm" onClick={() => setEditMode(true)}>
              Edit
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          {editMode ? (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Account Level</Form.Label>
                    <Form.Select
                      value={formData.account_level}
                      onChange={(e) => setFormData({ ...formData, account_level: e.target.value })}
                    >
                      {ACCOUNT_LEVELS.map(level => (
                        <option key={level.value} value={level.value}>
                          {level.label} ({formatNumber(level.tokens)} tokens/month)
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Seat Count</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={formData.seat_count}
                      onChange={(e) => setFormData({ ...formData, seat_count: parseInt(e.target.value) })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Timezone</Form.Label>
                    <Form.Select
                      value={formData.timezone}
                      onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    >
                      {timezones.map(tz => (
                        <option key={tz} value={tz}>{tz}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Markup (%)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="1"
                      value={formData.markup_percentage}
                      onChange={(e) => setFormData({ ...formData, markup_percentage: parseInt(e.target.value) || 0 })}
                    />
                    <Form.Text className="text-muted">
                      Enter as whole number. 100 = 100% = 2x cost, 200 = 200% = 3x cost. Stored as decimal (e.g. 200% → 2.00).
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Products Enabled</Form.Label>
                    {PRODUCTS.map(product => (
                      <Form.Check
                        key={product.id}
                        type="checkbox"
                        id={`edit-product-${product.id}`}
                        label={
                          <span>
                            {product.name}
                            {!product.available && <span className="ms-2 text-muted">(Coming Soon)</span>}
                          </span>
                        }
                        checked={formData.products_enabled.includes(product.id)}
                        onChange={() => handleProductToggle(product.id)}
                      />
                    ))}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Audio Mode</Form.Label>
                    <Form.Select
                      value={formData.audio_mode}
                      onChange={(e) => setFormData({ ...formData, audio_mode: e.target.value })}
                    >
                      <option value="summary">AI Summary</option>
                      <option value="reading_30s">Reading</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Controls how audio is generated for this tenant's articles.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {formData.audio_mode === 'reading_30s' ? 'Reading' : 'Summary'} Duration (seconds)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="10"
                      max="300"
                      value={formData.audio_mode === 'reading_30s' ? formData.audio_duration_reading : formData.audio_duration_summary}
                      onChange={(e) => setFormData({
                        ...formData,
                        [formData.audio_mode === 'reading_30s' ? 'audio_duration_reading' : 'audio_duration_summary']: parseInt(e.target.value) || 30
                      })}
                    />
                    <Form.Text className="text-muted">
                      Target duration for {formData.audio_mode === 'reading_30s' ? 'reading' : 'AI summary'} mode.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex gap-2">
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline-secondary" onClick={() => {
                  setEditMode(false);
                  setFormData({
                    name: tenant.name,
                    account_level: tenant.account_level,
                    products_enabled: tenant.settings?.products_enabled || [],
                    audio_mode: tenant.settings?.audio_mode || 'summary',
                    audio_duration_summary: tenant.settings?.audio_duration?.summary || 30,
                    audio_duration_reading: tenant.settings?.audio_duration?.reading_30s || 30,
                    markup_percentage: Math.round(parseFloat(tenant.markup_percentage || 0) * 100),
                    seat_count: tenant.seat_count,
                    timezone: tenant.timezone || 'America/New_York'
                  });
                }}>
                  Cancel
                </Button>
              </div>
            </Form>
          ) : (
            <Row>
              <Col md={6}>
                <dl>
                  <dt>Company Name</dt>
                  <dd>{tenant.name}</dd>

                  <dt>Slug</dt>
                  <dd><code>{tenant.slug}</code></dd>

                  <dt>Account Level</dt>
                  <dd><Badge bg="primary">{tenant.account_level}</Badge></dd>

                  <dt>Status</dt>
                  <dd>
                    {tenant.is_active ? (
                      <Badge bg="success">Active</Badge>
                    ) : (
                      <Badge bg="danger">Deactivated</Badge>
                    )}
                  </dd>
                </dl>
              </Col>
              <Col md={6}>
                <dl>
                  <dt>Timezone</dt>
                  <dd>{tenant.timezone || 'America/New_York'}</dd>

                  <dt>Seat Count</dt>
                  <dd>{tenant.seat_count}</dd>

                  <dt>Products Enabled</dt>
                  <dd>
                    {(tenant.settings?.products_enabled || []).map(p => (
                      <Badge key={p} bg="info" className="me-1">
                        {PRODUCTS.find(prod => prod.id === p)?.name || p}
                      </Badge>
                    ))}
                    {(!tenant.settings?.products_enabled || tenant.settings.products_enabled.length === 0) && (
                      <span className="text-muted">None</span>
                    )}
                  </dd>

                  <dt>Markup</dt>
                  <dd>
                    <Badge bg="warning" text="dark">
                      {Math.round(parseFloat(tenant.markup_percentage || 0) * 100)}%
                    </Badge>
                  </dd>

                  <dt>Audio Mode</dt>
                  <dd>
                    <Badge bg={tenant.settings?.audio_mode === 'reading_30s' ? 'secondary' : 'primary'}>
                      {tenant.settings?.audio_mode === 'reading_30s' ? 'Reading' : 'AI Summary'}
                    </Badge>
                    {' '}
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                      ({tenant.settings?.audio_duration?.[tenant.settings?.audio_mode || 'summary'] || 30}s)
                    </span>
                  </dd>

                  <dt>Created</dt>
                  <dd>{new Date(tenant.created_at).toLocaleDateString()}</dd>
                </dl>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Danger Zone */}
      {['super_admin', 'admin'].includes(auth.platform_role) && (
        <Card style={{ borderColor: '#ef4444' }}>
          <Card.Header style={{ background: '#fef2f2', borderColor: '#ef4444' }}>
            <h5 className="mb-0" style={{ color: '#dc2626' }}>Danger Zone</h5>
          </Card.Header>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <strong>Deactivate Tenant</strong>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                  Users will not be able to access this tenant. Data is preserved.
                </p>
              </div>
              {tenant.is_active ? (
                <Button variant="outline-danger" onClick={handleDeactivate}>
                  Deactivate
                </Button>
              ) : (
                <Button variant="outline-success" onClick={handleReactivate}>
                  Reactivate
                </Button>
              )}
            </div>

            {auth.platform_role === 'super_admin' && (
              <>
                <hr />
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Delete Tenant</strong>
                    <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                      Permanently delete this tenant and all associated data. This cannot be undone.
                    </p>
                  </div>
                  <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
                    Delete Tenant
                  </Button>
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton style={{ background: '#fef2f2' }}>
          <Modal.Title style={{ color: '#dc2626' }}>
            Permanently Delete Tenant
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            This will permanently delete <strong>"{tenant.name}"</strong> and ALL associated data including:
          </Alert>
          <ul>
            <li>{tenant.user_count || 0} users</li>
            <li>All articles and generated media</li>
            <li>All usage history and analytics</li>
            <li>All invitations and settings</li>
          </ul>
          <p><strong>THIS ACTION CANNOT BE UNDONE.</strong></p>
          <Form.Group>
            <Form.Label>To confirm, type the tenant name exactly:</Form.Label>
            <Form.Control
              type="text"
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              placeholder={tenant.name}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowDeleteModal(false);
            setDeleteConfirmName('');
          }}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={deleteConfirmName !== tenant.name}
          >
            Delete Permanently
          </Button>
        </Modal.Footer>
      </Modal>
    </TenantManagementLayout>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(TenantDetail);
